import { UserType } from '@inspectify/core';
import { Login } from '@mui/icons-material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Logout from '@mui/icons-material/Logout';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useColorMode } from '../styles/ThemeSwitcher';
import { clearMfa } from 'utils/mfa';

function AccountMenu() {
  const theme = useTheme();
  const navigate = useNavigate();
  const colorMode = useColorMode();
  const { userContext: { user, currentContractor, availableUsers }, setSelectedUserContractor } = useAuth();

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);

  const userInitials = user?.name?.split(' ').map((n: string) => n[0]).join('');

  const open = Boolean(menuAnchor);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleClose = () => {
    setMenuAnchor(null);
  };
  const handleLogout = async () => {
    handleClose();
    navigate(`/sign_out?from=${window.location.pathname}`);
  }
  const handleHelpDesk = () => {
    handleClose();
    window.open('https://knowledge.inspectify.com/', '_blank');
  }
  const handleToS = () => {
    handleClose();
    window.open('https://public-inspectify.s3.amazonaws.com/documents/Inspectify+-+Terms+of+Use.pdf', '_blank');
  }
  const handlePP = () => {
    handleClose();
    window.open('https://www.inspectify.com/company/privacy-policy', '_blank');
  }
  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    setMenuAnchor(null);
    clearMfa();
    setSelectedUserContractor(availableUsers?.[index] as UserType);
  };
  const handleSignIn = () => {
    handleClose();
    navigate(`/sign_in?from=${window.location.pathname}`);
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Tooltip title="Account settings">
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar sx={{ width: 32, height: 32 }}>{userInitials}</Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {availableUsers?.length ? (
          <>
            <Typography sx={{ pl: 1 }} variant="subtitle2">
              Accounts
            </Typography>
            {availableUsers?.map((item, index) => (
              item.contractor ? (
                <MenuItem
                  id={`account-${index}`}
                  key={item.contractor?.businessName || index}
                  selected={item.id === currentContractor?.id}
                  aria-haspopup="listbox"
                  aria-controls="lock-menu"
                  aria-label="accounts"
                  aria-expanded={open ? 'true' : undefined}
                  disabled={!item.contractor}
                  onClick={event => handleMenuItemClick(event, index)}
                >
                  <ListItemText
                    primary={item.contractor?.businessName}
                    secondary={`${item.contractor ? `${item.contractor?.city}, ${item.contractor?.state}` : ''}`}
                  />
                </MenuItem>
              ) : (
                <MenuItem
                  id={`account-${index}`}
                  key={`team-${item.teamUsers?.[0]?.team?.id}`}
                  selected={item.id === currentContractor?.id}
                  aria-haspopup="listbox"
                  aria-controls="lock-menu"
                  aria-label="accounts"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={event => handleMenuItemClick(event, index)}
                >
                  <ListItemText
                    primary={
                      item?.teamUsers?.[0]?.team?.company?.name
                      || user?.name
                      || user?.email
                    }
                  />
                </MenuItem>
              )
            ))}
            <Divider />
          </>
        ) : null}

        <MenuItem onClick={colorMode.toggleColorMode}>
          {theme.palette.mode === 'dark'
            ? <Brightness7Icon sx={{ mr: 1 }} />
            : <Brightness4Icon sx={{ mr: 1 }} />}
          {theme.palette.mode} theme
        </MenuItem>
        <MenuItem onClick={handleHelpDesk}>
          Help Center
        </MenuItem>
        <MenuItem onClick={handleToS}>
          Terms of use
        </MenuItem>
        <MenuItem onClick={handlePP}>
          Privacy policy
        </MenuItem>
        <Divider />
        {user ? (
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" color="primary" />
            </ListItemIcon>
            Logout
          </MenuItem>
        ) : (
          <MenuItem onClick={handleSignIn}>
            <ListItemIcon>
              <Login fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="Sign in" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

export default React.memo(AccountMenu);
